import { ReactNode } from 'react'

import { cn } from '../../lib/utils'
import { useLinkResolver } from '../context/useLinkResolver'

/** Consistent vertical rhythm. Blocks render flush; the SPACER block is the escape hatch. */
export const BlockSection = ({
    children,
    className,
    full = false,
}: {
    children: ReactNode
    className?: string
    full?: boolean
}) => (
    <section
        className={cn(full ? 'w-full' : 'mx-auto w-full max-w-6xl px-4 py-10 lg:py-14', className)}
    >
        {children}
    </section>
)

export const BlockTitle = ({
    children,
    className,
}: {
    children?: ReactNode
    className?: string
}) =>
    children ? (
        <h2 className={cn('text-2xl font-bold lg:text-4xl', className)}>{children}</h2>
    ) : null

export const BlockSubtitle = ({ children }: { children?: ReactNode }) =>
    children ? <p className="text-muted-foreground text-base lg:text-lg">{children}</p> : null

export type CtaProps = {
    label?: string | null
    link?: string | null
    variant?: 'primary' | 'secondary'
    className?: string
}

/**
 * Degrades to a plain span when the link is missing or its target was deleted, rather than
 * emitting an anchor with an empty href.
 */
export const BlockCta = ({ label, link, variant = 'primary', className }: CtaProps) => {
    const { resolveLinkProps } = useLinkResolver()
    if (!label) return null

    const resolved = link ? resolveLinkProps(link) : null
    const classes = cn(
        'inline-flex items-center justify-center rounded-md px-5 py-2.5 text-sm font-semibold transition-colors',
        variant === 'primary'
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'border-input hover:bg-accent border bg-transparent',
        className,
    )

    if (!resolved?.href) return <span className={classes}>{label}</span>
    return (
        <a {...resolved} className={classes}>
            {label}
        </a>
    )
}

export const byOrder = <T extends { order: number }>(items: readonly T[]): T[] =>
    [...items].sort((a, b) => a.order - b.order)
